import { create } from "zustand";
import { hc } from "@/frontend/lib/hc";
import {
  DEFAULT_WEBSITE_WIDGET_SETTINGS,
  type WebsiteWidgetSettings,
} from "@/shared/website-widget";

type ConversationLabelMode = "single" | "multiple";
type ContextualFlowIntervalMinutes = 10 | 20 | 30 | 60;

interface WebsiteWidgetState extends WebsiteWidgetSettings {
  scriptUrl: string;
}

interface OrgSettingsState {
  conversationLabelMode: ConversationLabelMode;
  timelinesEnabled: boolean;
  timezone: string;
  leadStageAiSuggestionsEnabled: boolean;
  leadStageAiAutoConfirmEnabled: boolean;
  salesDealsEnabled: boolean;
  salesCompaniesEnabled: boolean;
  contextualFlowIntervalMinutes: ContextualFlowIntervalMinutes;
  quicklinkBaseUrl: string;
  transcriptionContext: string;
  pdfTranscriptionEnabled: boolean;
  spreadsheetTranscriptionEnabled: boolean;
  websiteWidget: WebsiteWidgetState;
  loading: boolean;
  fetched: boolean;
  fetchSettings: () => Promise<void>;
  fetchWebsiteWidget: () => Promise<boolean>;
  setConversationLabelMode: (mode: ConversationLabelMode) => Promise<boolean>;
  setTimelinesEnabled: (enabled: boolean) => Promise<boolean>;
  setTimezone: (timezone: string) => Promise<boolean>;
  setLeadStageAiSuggestionsEnabled: (enabled: boolean) => Promise<boolean>;
  setLeadStageAiAutoConfirmEnabled: (enabled: boolean) => Promise<boolean>;
  setSalesDealsEnabled: (enabled: boolean) => Promise<boolean>;
  setSalesCompaniesEnabled: (enabled: boolean) => Promise<boolean>;
  setContextualFlowIntervalMinutes: (
    minutes: ContextualFlowIntervalMinutes,
  ) => Promise<boolean>;
  setQuicklinkBaseUrl: (url: string) => Promise<boolean>;
  setTranscriptionContext: (context: string) => Promise<boolean>;
  setPdfTranscriptionEnabled: (enabled: boolean) => Promise<boolean>;
  setSpreadsheetTranscriptionEnabled: (enabled: boolean) => Promise<boolean>;
  updateWebsiteWidget: (
    widget: Partial<WebsiteWidgetSettings>,
  ) => Promise<boolean>;
  reset: () => void;
}

export const useOrgSettingsStore = create<OrgSettingsState>((set) => ({
  conversationLabelMode: "multiple",
  timelinesEnabled: true,
  timezone: "Asia/Kolkata",
  leadStageAiSuggestionsEnabled: false,
  leadStageAiAutoConfirmEnabled: false,
  salesDealsEnabled: false,
  salesCompaniesEnabled: false,
  contextualFlowIntervalMinutes: 10,
  quicklinkBaseUrl: "",
  transcriptionContext: "",
  pdfTranscriptionEnabled: false,
  spreadsheetTranscriptionEnabled: false,
  websiteWidget: {
    ...DEFAULT_WEBSITE_WIDGET_SETTINGS,
    scriptUrl: "/widget-sdk/tldr-wa-widget.js",
  },
  loading: false,
  fetched: false,

  fetchSettings: async () => {
    set({ loading: true });
    try {
      const response = await hc.api["org-settings"].$get();
      const data: {
        success: boolean;
        data: Record<string, unknown> & {
          websiteWidget?: Partial<WebsiteWidgetState>;
        };
      } = await response.json();
      if (data.success) {
        set({
          conversationLabelMode: (data.data.conversationLabelMode as ConversationLabelMode) ?? "multiple",
          timelinesEnabled: (data.data.timelinesEnabled as boolean) ?? true,
          timezone: (data.data.timezone as string) ?? "Asia/Kolkata",
          leadStageAiSuggestionsEnabled:
            (data.data.leadStageAiSuggestionsEnabled as boolean) ?? false,
          leadStageAiAutoConfirmEnabled:
            (data.data.leadStageAiAutoConfirmEnabled as boolean) ?? false,
          salesDealsEnabled: (data.data.salesDealsEnabled as boolean) ?? false,
          salesCompaniesEnabled: (data.data.salesCompaniesEnabled as boolean) ?? false,
          contextualFlowIntervalMinutes:
            (data.data.contextualFlowIntervalMinutes as ContextualFlowIntervalMinutes) ?? 10,
          quicklinkBaseUrl: (data.data.quicklinkBaseUrl as string) ?? "",
          transcriptionContext: (data.data.transcriptionContext as string) ?? "",
          pdfTranscriptionEnabled: (data.data.pdfTranscriptionEnabled as boolean) ?? false,
          spreadsheetTranscriptionEnabled:
            (data.data.spreadsheetTranscriptionEnabled as boolean) ?? false,
          websiteWidget: {
            ...DEFAULT_WEBSITE_WIDGET_SETTINGS,
            scriptUrl: "/widget-sdk/tldr-wa-widget.js",
            ...data.data.websiteWidget,
          },
          fetched: true,
        });
      }
    } catch (error) {
      console.error("Error fetching org settings:", error);
    } finally {
      set({ loading: false });
    }
  },

  fetchWebsiteWidget: async () => {
    try {
      const response = await fetch("/api/org-settings/website-widget", { credentials: "include" });
      if (!response.ok) return false;
      const data = await response.json() as { success: boolean; data?: { websiteWidget?: Partial<WebsiteWidgetState> } };
      if (data.success && data.data?.websiteWidget) {
        set((state) => ({ websiteWidget: { ...state.websiteWidget, ...data.data!.websiteWidget } }));
        return true;
      }
    } catch {
      // The settings page reports the load failure to the user.
    }
    return false;
  },

  setConversationLabelMode: async (mode: ConversationLabelMode) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { conversationLabelMode: mode },
      });
      const data = await response.json();
      if (data.success) {
        set({ conversationLabelMode: mode });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating org settings:", error);
      return false;
    }
  },

  setTimelinesEnabled: async (enabled: boolean) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { timelinesEnabled: enabled } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ timelinesEnabled: enabled });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating timelines setting:", error);
      return false;
    }
  },

  setTimezone: async (timezone: string) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { timezone } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ timezone });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating organization timezone:", error);
      return false;
    }
  },

  setLeadStageAiSuggestionsEnabled: async (enabled: boolean) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { leadStageAiSuggestionsEnabled: enabled } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ leadStageAiSuggestionsEnabled: enabled });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating lead stage AI suggestions setting:", error);
      return false;
    }
  },

  setLeadStageAiAutoConfirmEnabled: async (enabled: boolean) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { leadStageAiAutoConfirmEnabled: enabled } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ leadStageAiAutoConfirmEnabled: enabled });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating automatic lane moves setting:", error);
      return false;
    }
  },

  setSalesDealsEnabled: async (enabled: boolean) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { salesDealsEnabled: enabled } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ salesDealsEnabled: enabled });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating deal visibility:", error);
      return false;
    }
  },

  setSalesCompaniesEnabled: async (enabled: boolean) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { salesCompaniesEnabled: enabled } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ salesCompaniesEnabled: enabled });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating company visibility:", error);
      return false;
    }
  },

  setContextualFlowIntervalMinutes: async (minutes: ContextualFlowIntervalMinutes) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: {
          contextualFlowIntervalMinutes: minutes,
        } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ contextualFlowIntervalMinutes: minutes });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating contextual flow interval:", error);
      return false;
    }
  },

  setQuicklinkBaseUrl: async (url: string) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { quicklinkBaseUrl: url } as Record<string, unknown>,
      });
      const data = await response.json();
      if (data.success) {
        set({ quicklinkBaseUrl: url });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating Quicklink:", error);
      return false;
    }
  },

  setTranscriptionContext: async (context: string) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: { transcriptionContext: context } as Record<string, unknown>,
      });
      const data: {
        success: boolean;
        data?: { transcriptionContext?: string };
      } = await response.json();
      if (data.success) {
        set({
          transcriptionContext:
            data.data?.transcriptionContext ?? context.trim(),
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating transcription context:", error);
      return false;
    }
  },

  setPdfTranscriptionEnabled: async (enabled: boolean) => {
    try {
      const response = await hc.api["org-settings"]["pdf-transcription"].$put({
        json: { enabled },
      });
      const data = await response.json();
      if (data.success) {
        set({ pdfTranscriptionEnabled: enabled });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating PDF transcription setting:", error);
      return false;
    }
  },

  setSpreadsheetTranscriptionEnabled: async (enabled: boolean) => {
    try {
      const response = await hc.api["org-settings"]["spreadsheet-transcription"].$put({
        json: { enabled },
      });
      const data = await response.json();
      if (data.success) {
        set({ spreadsheetTranscriptionEnabled: enabled });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating spreadsheet transcription setting:", error);
      return false;
    }
  },

  updateWebsiteWidget: async (widget: Partial<WebsiteWidgetSettings>) => {
    try {
      const response = await hc.api["org-settings"].$put({
        json: {
          websiteWidget: widget,
        } as Record<string, unknown>,
      });
      const data: {
        success: boolean;
        data?: {
          websiteWidget?: Partial<WebsiteWidgetState>;
        };
      } = await response.json();
      if (data.success) {
        set((state) => ({
          websiteWidget: {
            ...state.websiteWidget,
            ...widget,
            ...data.data?.websiteWidget,
          },
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating website widget settings:", error);
      return false;
    }
  },

  reset: () => {
    set({
      conversationLabelMode: "multiple",
      timelinesEnabled: true,
      timezone: "Asia/Kolkata",
      leadStageAiSuggestionsEnabled: false,
      leadStageAiAutoConfirmEnabled: false,
      salesDealsEnabled: false,
      salesCompaniesEnabled: false,
      contextualFlowIntervalMinutes: 10,
      quicklinkBaseUrl: "",
      transcriptionContext: "",
      pdfTranscriptionEnabled: false,
      spreadsheetTranscriptionEnabled: false,
      websiteWidget: {
        ...DEFAULT_WEBSITE_WIDGET_SETTINGS,
        scriptUrl: "/widget-sdk/tldr-wa-widget.js",
      },
      loading: false,
      fetched: false,
    });
  },
}));
